<template>
  <div class="preika-dashboard">
    <section class="hero-panel">
      <div class="hero-panel__glow hero-panel__glow--one"></div>
      <div class="hero-panel__glow hero-panel__glow--two"></div>

      <div class="hero-panel__content">
        <div class="hero-brand">
          <img
            src="/img/avatars/preika_title.png"
            :alt="productName"
            class="hero-brand__logo"
          >
          <!--
            **英語の飾りをやめた。**WORKPLACE PLATFORM は言い過ぎで、
            給与など別の製品を出したときに、どちらも同じ名乗りになる。
            何ができるかを、そのまま書く。
          -->
          <!--
            **メニューに実際にある名前だけを書く。**「日報」と書いていたが、
            そんな画面は無い（あるのは「業務報告」）。確かめずに置いた。
          -->
          <span class="hero-brand__badge">申請・経費・業務報告</span>
        </div>

        <!--
          日本語で書く。Good morning を直に置いていて、しかも氏名が
          呼び捨てだった。毎朝、全員が見るところ。
        -->
        <p class="hero-panel__eyebrow">{{ greeting }}</p>
        <h1 class="hero-panel__title">
          今日の仕事を、<br>
          <span>ひとつの画面から。</span>
        </h1>
        <!--
          **メニューにある名前で書く。**「月次報告」という画面は無い。
          あるのは「業務報告」と、事務・管理向けの「月次集計」。
          言い換え（スムーズに・シンプルに）を重ねても、何ができるかは
          増えない。することを、そのまま書く。
        -->
        <p class="hero-panel__description">
          勤務表と業務報告、休暇の申請から経費の精算まで。
          今日やることを{{ productName }}にまとめています。
        </p>

        <div class="hero-panel__meta">
          <div class="hero-meta-item">
            <CIcon name="cil-calendar"/>
            <span>{{ formattedDate }}</span>
          </div>
          <div class="hero-meta-item">
            <CIcon name="cil-clock"/>
            <span>{{ formattedTime }}</span>
          </div>
        </div>
      </div>

      <div class="hero-visual" aria-hidden="true">
        <div class="hero-visual__orbit hero-visual__orbit--one"></div>
        <div class="hero-visual__orbit hero-visual__orbit--two"></div>
        <div class="hero-visual__card hero-visual__card--rear">
          <span class="visual-line visual-line--short"></span>
          <span class="visual-line"></span>
          <span class="visual-line visual-line--middle"></span>
        </div>
        <div class="hero-visual__card hero-visual__card--front">
          <div class="visual-card__header">
            <span class="visual-avatar">P</span>
            <span class="visual-status"></span>
          </div>
          <div class="visual-chart">
            <span style="height: 36%"></span>
            <span style="height: 58%"></span>
            <span style="height: 45%"></span>
            <span style="height: 78%"></span>
            <span style="height: 64%"></span>
            <span style="height: 92%"></span>
          </div>
          <div class="visual-card__footer">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </section>

    <section class="dashboard-grid dashboard-grid--top">
      <article class="dashboard-card attendance-card">
        <div class="card-heading">
          <div>
            <p class="card-heading__eyebrow">ATTENDANCE</p>
            <h2>打刻</h2>
          </div>
          <span
            class="status-pill"
            :class="{
              'status-pill--active': attendanceStatus === 'WORKING' && !stampingStatusError,
               'status-pill--break': attendanceStatus === 'BREAK' && !stampingStatusError,
               'status-pill--scheduled': attendanceStatus === 'SCHEDULED' && !stampingStatusError,
              'status-pill--error': stampingStatusError
            }"
          >
            <span class="status-pill__dot"></span>
            {{ attendanceStatusLabel }}
          </span>
        </div>

        <div class="attendance-time">
          <strong>{{ formattedTime }}</strong>
          <span>{{ formattedDateWithWeekday }}</span>
        </div>

        <div class="attendance-actions">
          <button
            type="button"
            class="attendance-button attendance-button--start"
            :disabled="loading || stampingStatusLoading || !canStartAttendance"
            @click="handleTime('start')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-media-play"/></span>
            <span><strong>出勤</strong><small>業務を開始する</small></span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--end"
            :disabled="loading || stampingStatusLoading || !canEndAttendance"
            @click="handleTime('end')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-media-stop"/></span>
            <span><strong>退勤</strong><small>業務を終了する</small></span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--break"
            :disabled="loading || stampingStatusLoading || !canStartBreak"
            @click="handleTime('breakStart')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-coffee"/></span>
            <span>
              <strong>{{ breakStartButtonLabel }}</strong>
              <small>{{ breakStartDescription }}</small>
            </span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--return"
            :disabled="loading || stampingStatusLoading || !canEndBreak"
            @click="handleTime('breakEnd')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-loop"/></span>
            <span>
              <strong>{{ breakEndButtonLabel }}</strong>
              <small>{{ breakEndDescription }}</small>
            </span>
          </button>
        </div>

        <div
          v-if="stampingStatusLoaded && useProjectBreakFlg"
          class="attendance-break-note"
        >
          <strong>固定休憩は出勤時に反映済みです。</strong>
          <span>追加で発生した休憩だけを開始・終了してください。</span>
        </div>

        <section
          v-if="stampingStatusLoaded"
          class="attendance-stamp-result"
          aria-labelledby="attendance-stamp-result-title"
          aria-live="polite"
        >
          <div class="attendance-stamp-result__header">
            <div>
              <p>STAMP RESULT</p>
              <h3 id="attendance-stamp-result-title">打刻結果</h3>
            </div>
            <span v-if="businessDate" class="attendance-stamp-result__date">
              勤務日 {{ formatBusinessDate(businessDate) }}
            </span>
          </div>
          <div v-if="attendanceStatus === 'SCHEDULED'" class="attendance-scheduled-note">
            勤務表で事前保存された定時候補です。出勤ボタンで現在時刻の実績へ切り替えます。
          </div>
          <div class="attendance-stamp-result__grid">
            <div class="attendance-stamp-result__item attendance-stamp-result__item--start">
              <span class="attendance-stamp-result__label">出勤</span>
              <strong>{{ startTime || '--:--' }}</strong>
              <small>勤務開始</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--end">
              <span class="attendance-stamp-result__label">退勤</span>
              <strong>{{ endTimeDisplay }}</strong>
              <small>勤務終了</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--break">
              <span class="attendance-stamp-result__label">休憩合計</span>
              <strong>{{ breakTime || '00:00' }}</strong>
              <small>{{ breakSummaryDescription }}</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--night">
              <span class="attendance-stamp-result__label">夜勤休憩</span>
              <strong>{{ nightRest || '00:00' }}</strong>
              <small>22:00〜05:00</small>
            </div>
          </div>
        </section>

        <div
          v-else-if="stampingStatusError"
          class="attendance-status-error"
          role="alert"
        >
          <div>
            <strong>打刻情報を取得できませんでした</strong>
            <small>安全のため打刻ボタンを無効にしています。</small>
          </div>
          <button
            type="button"
            :disabled="stampingStatusLoading"
            @click="fetchStampingStatus"
          >
            再取得
          </button>
        </div>

        <div v-if="loading" class="attendance-loading">
          <span class="attendance-loading__spinner"></span>
          打刻情報を送信しています
        </div>
      </article>

      <article class="dashboard-card quick-card">
        <div class="card-heading">
          <div>
            <p class="card-heading__eyebrow">QUICK ACCESS</p>
            <h2>よく使うメニュー</h2>
          </div>
          <CIcon name="cil-apps" class="card-heading__icon"/>
        </div>

        <div class="quick-menu">
          <router-link
            v-for="item in quickMenu"
            :key="item.label"
            :to="item.to"
            class="quick-menu__item"
          >
            <span class="quick-menu__icon" :class="'quick-menu__icon--' + item.tone">
              <CIcon :name="item.icon"/>
            </span>
            <span class="quick-menu__text">
              <strong>{{ item.label }}</strong>
              <small>{{ item.description }}</small>
            </span>
            <CIcon name="cil-chevron-right" class="quick-menu__arrow"/>
          </router-link>
        </div>
      </article>
    </section>

    <section class="metric-grid">
      <router-link to="/submission_approval/report_list" class="metric-card metric-card--blue">
        <span class="metric-card__icon"><CIcon name="cil-inbox"/></span>
        <span class="metric-card__body"><small>未申請</small><strong>{{ actionCounts.unsubmittedCount || 0 }}</strong><span>今月の提出物</span></span>
      </router-link>
      <router-link to="/submission_approval/report_list" class="metric-card metric-card--orange">
        <span class="metric-card__icon"><CIcon name="cil-warning"/></span>
        <span class="metric-card__body"><small>差戻し</small><strong>{{ actionCounts.rejectedCount || 0 }}</strong><span>再対応が必要</span></span>
      </router-link>
      <router-link to="/submission_approval/submission_management" class="metric-card metric-card--purple">
        <span class="metric-card__icon"><CIcon name="cil-check-circle"/></span>
        <span class="metric-card__body"><small>承認待ち</small><strong>{{ actionCounts.approvalWaitingCount || 0 }}</strong><span>確認が必要な申請</span></span>
      </router-link>
      <div class="metric-card metric-card--green">
        <span class="metric-card__icon"><CIcon name="cil-clock"/></span>
        <span class="metric-card__body"><small>今月の勤務時間</small><strong class="metric-card__time">{{ formatDuration(monthlySummary.workTime) }}</strong><span>{{ monthlySummary.attendanceDays || 0 }}日 出勤</span></span>
      </div>
    </section>

    <section class="preika-insight-layout preika-insight-layout--personal">
      <DashboardActionCenter
        :items="actionItems"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
      <DashboardWorkProjection
        :projection="workProjection"
        :management-view="managementView"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
    </section>

    <section
      v-if="managementView"
      class="preika-insight-layout preika-insight-layout--management"
    >
      <DashboardCloseCockpit
        :summary="closeCockpit"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
        @page="fetchDashboardInsights"
        @condition="applyCloseCondition"
      />
      <DashboardApprovalAging
        :aging="approvalAging"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
    </section>

    <!--
      今日の勤怠状況。**経営管理者以上だけ。**
      権限が無ければ問い合わせもしない。呼んで403にすると、利用者からは
      壊れて見える（ダッシュボードの打刻カードで同じことが起きている）。
    -->
    <section v-if="attendanceOverviewView" class="preika-insight-layout">
      <DashboardAttendanceOverview
        :overview="attendanceOverview"
        :keyword="attendanceOverviewKeyword"
        :status-filter="attendanceOverviewStatus"
        :loading="attendanceOverviewLoading"
        :error="attendanceOverviewError"
        @retry="fetchAttendanceOverview"
        @page="fetchAttendanceOverview"
        @filter="applyAttendanceOverviewFilter"
      />
    </section>

    <!--
      36協定の上限に触れている方。**経営管理者以上だけ。**

      フラグは attendanceOverviewView と同じ1回の /access-context から作る。
      **managementView を使わない。**あれは data.managementView が false でも
      authorityName が admin か clerk なら true に倒れるので、事務担当者にも
      出てしまう。

      **該当者が0名なら描かない。**出ていること自体が信号になる。
      判定できなかった方も「触れている」に含めて数えるので、
      見ていないことが黙って消えることはない。
    -->
    <!--
      **届いていないことは、カードが出ない月にも出す。**

      カードは該当者が0名なら描かない（出ていること自体が信号）。だが
      通知先を1つも決めていない会社は、まさにその月に何も出ないまま
      翌月へ行く。次の月に超過者が出ても、お知らせは1通も届かない——
      その会社が気づく機会は、ここに帯を出す以外に無い。

      値は同じ1回の応答に既に入っている。出すだけで塞がる。
    -->
    <div
      v-if="overtimeLimitView && overtimeLimitDeliveryNotice"
      class="preika-delivery-notice"
      :class="'preika-delivery-notice--' + overtimeLimitDeliveryNotice.tone"
      role="status"
    >
      <CIcon name="cil-bell"/>
      <div>
        <strong>{{ overtimeLimitDeliveryNotice.headline }}</strong>
        <small>{{ overtimeLimitDeliveryNotice.detail }}</small>
      </div>
    </div>

    <section
      v-if="overtimeLimitView && overtimeLimitTotal > 0"
      class="preika-insight-layout"
    >
      <DashboardOvertimeLimit :status="overtimeLimitStatus"/>
    </section>

    <section class="dashboard-grid dashboard-grid--overview">
      <article class="dashboard-card monthly-summary-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">MONTHLY PERFORMANCE</p><h2>今月の勤務サマリー</h2></div>
          <span class="summary-month">{{ currentYearMonth }}</span>
        </div>
        <div class="summary-list">
          <div v-for="item in summaryItems" :key="item.label" class="summary-item">
            <span class="summary-item__label">{{ item.label }}</span><strong>{{ item.value }}</strong>
          </div>
        </div>
        <!--
          **時間外の数字のすぐ下に置く。**別のカードへ離すと、
          「時間外 36時間00分」を見た人が、限度に対してどこなのかを
          別の場所へ探しにいくことになる。

          限度が分からない会社（36協定が未登録・法令判定が切ってある）
          では、部品の側が自分で出ないと決める。
        -->
        <DashboardMyOvertimeGauge
          class="monthly-summary-card__gauge"
          :overtime-minutes="myOvertimeMinutes"
          :limit-minutes="myOvertimeLimitMinutes"
          :threshold-percent="myOvertimeThresholdPercent"
          :incomplete="myOvertimeIncomplete"
        />
      </article>

      <article class="dashboard-card monthly-status-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">MONTHLY STATUS</p><h2>月次提出状況</h2></div>
          <router-link to="/submission_approval/report_list" class="text-link">一覧を見る<CIcon name="cil-arrow-right"/></router-link>
        </div>
        <div class="document-status-list">
          <div v-for="document in monthlyDocuments" :key="document.documentType" class="document-status">
            <span class="document-status__icon"><CIcon :name="document.icon"/></span>
            <span class="document-status__name">{{ document.documentName }}</span>
            <span class="document-status__badge" :class="'document-status__badge--' + document.tone">{{ document.statusName }}</span>
          </div>
        </div>
      </article>
    </section>

    <!--
      お知らせが1件も無ければ、カードごと出さない。**空のカードは
      「何かあるはずなのに無い」に見える。**そのぶん提出ガイドを広げる。
    -->
    <section class="dashboard-grid dashboard-grid--middle"
             :class="{ 'dashboard-grid--single': notices.length === 0 }">
      <article v-if="notices.length" class="dashboard-card notice-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">INFORMATION</p><h2>お知らせ</h2></div>
          <span class="notice-count">{{ notices.length }} NEWS</span>
        </div>
        <div class="notice-list">
          <div v-for="notice in notices" :key="notice.id" class="notice-item">
            <div class="notice-item__date">{{ noticeDate(notice) }}</div>
            <div class="notice-item__content">
              <span class="notice-item__category" :class="'notice-item__category--' + noticeTone(notice)">{{ notice.category }}</span>
              <p>{{ notice.body }}</p>
            </div>
          </div>
        </div>
      </article>

      <article class="dashboard-card submission-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">SUBMISSION GUIDE</p><h2>提出ガイド</h2></div>
          <CIcon name="cil-paper-plane" class="card-heading__icon"/>
        </div>
        <div class="submission-guide">
          <!--
            月次の締めは会社ごと。**決めていない会社では、この枠ごと
            出さない。**2026-08-27 まで「第2営業日まで」と直に書かれて
            いて、これは販売元の締めだった。他社の社員は毎日、自社と
            違う期限を見せられていた。
          -->
          <div v-if="submissionDue" class="submission-guide__item submission-guide__item--monthly">
            <span class="submission-guide__number">{{ submissionDue.number }}</span>
            <div>
              <strong>{{ submissionDue.label }}</strong>
              <p v-if="submissionDue.items">{{ submissionDue.items }}</p>
            </div>
          </div>
          <div class="submission-guide__item submission-guide__item--anytime">
            <span class="submission-guide__number"><CIcon name="cil-bell"/></span>
            <div><strong>わかり次第、早めに</strong><p>休暇・予定外時間外・休日出勤</p></div>
          </div>
        </div>
        <router-link to="/submission_approval/report_list" class="submission-link">承認申請一覧を確認<CIcon name="cil-arrow-right"/></router-link>
      </article>
    </section>

    <!--
      社内カレンダー。**入れていない会社では、節ごと出さない。**

      2026-08-27 まで、ここには販売元（AKI）の Google カレンダーが直に
      書かれていて、v-if も無かった。買った会社の社員が自社の画面を
      開くと、こちらの社内予定が見えていた。

      **base64 で URL に埋まっていたので、画面のドメイン検査は素通りした。**
    -->
    <section v-if="calendarEmbedUrl" class="dashboard-card calendar-card">
      <div class="card-heading calendar-card__heading">
        <div><p class="card-heading__eyebrow">SCHEDULE</p><h2>社内カレンダー</h2></div>
      </div>
      <div class="calendar-frame">
        <iframe
          :src="calendarEmbedUrl"
          title="社内カレンダー"
          frameborder="0"
          scrolling="no"
        ></iframe>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { PRODUCT_NAME } from '../common/productName'
import moment from 'moment'
import ConstList from './../ConstList'
import { normalizeTimeValue } from '../utils/timeValue'
import { isDashboardManagementAuthority } from '../utils/dashboardInsights'
import {
  defaultOvertimeLimitMonth,
  overtimeLimitConditionParams,
  overtimeAlertDeliveryNotice
} from '../utils/overtimeLimitStatus'
import { backendErrorMessage } from '../utils/rosterActualPolicy'
import DashboardActionCenter from '../common/dashboard/DashboardActionCenter'
import DashboardWorkProjection from '../common/dashboard/DashboardWorkProjection'
import DashboardCloseCockpit from '../common/dashboard/DashboardCloseCockpit'
import DashboardApprovalAging from '../common/dashboard/DashboardApprovalAging'
import DashboardAttendanceOverview from '../common/dashboard/DashboardAttendanceOverview'
import DashboardOvertimeLimit from '../common/dashboard/DashboardOvertimeLimit'
import DashboardMyOvertimeGauge from '../common/dashboard/DashboardMyOvertimeGauge'
import { safeErrorDigest } from '../utils/safeResponseMessage'

export default {
  name: 'Dashboard',

  components: {
    DashboardActionCenter,
    DashboardWorkProjection,
    DashboardCloseCockpit,
    DashboardApprovalAging,
    DashboardAttendanceOverview,
    DashboardOvertimeLimit,
    DashboardMyOvertimeGauge
  },

  data () {
    return {
      now: new Date(), timerId: null, loading: false, submitFlg: false,
      useProjectBreakFlg: false, userName: '',
      attendanceStatus: 'NOT_STARTED', businessDate: '',
      startTime: '', endTime: '',
      breakStartedAt: '', breakTime: '00:00', nightRest: '00:00',
      stampingStatusLoaded: false, stampingStatusLoading: false,
      stampingStatusError: false,
      insightLoading: false, insightError: false,
      /*
       * **応答は、最後に投げた要求のものだけを反映する。**
       *
       * 検索語を変えてすぐ絞り込みを変えると、先に投げた要求の応答が
       * 後から届くことがある。画面の条件は新しいのに一覧が古い、という
       * ずれ方をする。連番を持ち、番号が違う応答は捨てる。
       */
      insightRequestId: 0, attendanceOverviewRequestId: 0,
      authorityName: '', managementView: false, actionItems: [],
      workProjection: {}, closeCockpit: {}, approvalAging: {},
      // 今日の勤怠状況。経営管理者以上だけ。managementView より狭い。
      attendanceOverviewView: false, attendanceOverview: {},
      /*
       * 36協定の上限。今日の勤怠状況と同じ狭さで、同じ1回の
       * /access-context から決める。**dashboard/insights へ相乗りさせない。**
       * あちらは締めの「次へ」で何度も取り直すので、相乗りさせると
       * 全社ぶんの上限判定がそのたびに走る。
       */
      overtimeLimitView: false, overtimeLimitStatus: {},
      // 自分の1か月の限度時間と、会社が決めた予告の割合。
      // **協定が無ければ空のまま**——45時間と決め打ちで描かない。
      myOvertimeLimit: {},
      attendanceOverviewLoading: false, attendanceOverviewError: false,
      // 絞り込みの条件は親が持つ。サーバへ渡して全員から絞るため。
      attendanceOverviewKeyword: '', attendanceOverviewStatus: '',
      // 月次締めも同じ。既定は未完了（コックピットのチップの初期値と揃える）。
      closeQuery: '', closeState: 'INCOMPLETE',
      actionCounts: {}, monthlySummary: {}, submissions: [],
      quickMenu: [
        { label: '勤務表', description: '日々の勤務実績を入力', to: '/report/roster', icon: 'cil-calendar-check', tone: 'blue' },
        { label: '業務報告', description: '月次の業務内容を報告', to: '/report/monthlyreport', icon: 'cil-description', tone: 'navy' },
        { label: '経費精算', description: '業務経費を登録・申請', to: '/payoff/expense', icon: 'cil-wallet', tone: 'green' },
        { label: '通勤費精算', description: '通勤経路・交通費を登録', to: '/payoff/travelCost', icon: 'cil-bus-alt', tone: 'cyan' },
        { label: '休暇申請', description: '有休・代休・特休・欠勤を申請', to: '/paidandsubstitutes/list', icon: 'cil-sun', tone: 'orange' },
        { label: '承認申請一覧', description: '提出状況を確認', to: '/submission_approval/report_list', icon: 'cil-send', tone: 'purple' },
        { label: '承認待ち一覧', description: '確認が必要な申請', to: '/submission_approval/submission_management', icon: 'cil-check-circle', tone: 'cyan' }
      ],
      /** 会社ごとの社内カレンダー。空なら節ごと出さない。 */
      calendarEmbedUrl: '',
      /* 月次提出の締め。**空は「決めていない」。** */
      submissionDueKind: '',
      submissionDueDay: null,
      submissionDueItems: '',
      /*
       * 運営からのお知らせ。**サーバから来たものだけを出す。**
       *
       * 2026-08-27 まで、ここには3件が直に書かれていた。うち2件は
       * 2025.09.10 の「〜になりました」で、1年近く前の知らせを
       * 新しく契約した会社の社員が毎日見ていた。既定を持たせない。
       */
      notices: []
    }
  },
  computed: {
    /**
     * 今月の法定時間外労働（分）。
     *
     * **サマリーの「時間外」は使わない。**あれは画面が日次を合計して
     * 保存した値（t_monthly.sum_over_time）で、休日労働と同じ時間が
     * 両方に入るうえ、**週40時間（労基法32条1項）の超過が入っていない。**
     * 日8時間ずつ週6日の人は「時間外0時間」になる。
     *
     * 36協定の限度時間と並べる数字なので、判定と同じ定義でなければ
     * 意味が無い。サーバが判定と同じ口から取った値をそのまま使う。
     */
    myOvertimeMinutes () {
      const value = Number(this.myOvertimeLimit.overtimeMinutes)
      return Number.isFinite(value) && value >= 0 ? value : null
    },

    /**
     * 読み取れていない日があるか。
     *
     * <b>合計は小さいほうへずれる。</b>「まだ余裕がある」と見えるほうへ
     * 倒れるので、そのときは数字だけを黙って出さない。
     */
    myOvertimeIncomplete () {
      return this.myOvertimeLimit.incomplete === true
    },

    /**
     * 1か月の限度時間（分）。
     *
     * **45時間と決め打ちしない。**1年単位の変形労働時間制なら42時間で、
     * 協定で別の値を決めている会社もある。協定が無ければ null で、
     * カードそのものが出ない。
     */
    myOvertimeLimitMinutes () {
      const value = Number(this.myOvertimeLimit.limitMinutes)
      return value > 0 ? value : null
    },

    /** 会社が決めた予告の割合。決めていなければ null で、線を引かない。 */
    myOvertimeThresholdPercent () {
      const value = Number(this.myOvertimeLimit.thresholdPercent)
      return value > 0 ? value : null
    },

    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    /**
     * 36協定の上限に触れている方の合計。
     *
     * **サーバが数えた合計をそのまま使う。**画面で数え直すと、
     * カードに並べた3名だけを見て「3名」と出す形になる。
     * 判定できなかった方もこの合計に入っているので、
     * 見ていないことがあればカードは出る。
     */
    overtimeLimitTotal () {
      return Number(this.overtimeLimitStatus.totalCount) || 0
    },

    /**
     * 上限のお知らせが届いていないこと。**カードとは別に出す。**
     *
     * 幹部が毎日開くのはこの画面で、専用画面はお知らせを受けてから開く。
     * つまり届いていない会社では専用画面が開かれない。**届いていないと
     * いちばん伝わらない相手が、いちばん見ている画面で黙っていた。**
     *
     * 応答が返っていないあいだ（blocker が空）は黙る。読み込み中の一瞬に
     * 「届いていません」を出すと、正しく届いている会社にも毎回見える。
     */
    overtimeLimitDeliveryNotice () {
      const status = this.overtimeLimitStatus || {}
      if (!status.notificationBlocker) return null
      return overtimeAlertDeliveryNotice(
        status.notificationBlocker,
        status.notificationUsingSharedChannel === true,
        status.notificationLastSendFailed === true
          ? (status.notificationLastFailedMonth || '')
          : ''
      )
    },

    /**
     * 提出ガイドに出す、月次の締め。
     *
     * **数え方と日が揃っていなければ null。**揃っていない状態を画面で
     * 補うと、どちらか一方だけ設定した会社に中途半端な案内が出る。
     * サーバもDBも対で縛っているので、ここも対で見る。
     */
    submissionDue () {
      const day = Number(this.submissionDueDay)
      if (!this.submissionDueKind || !day) { return null }
      const label = this.submissionDueKind === 'BUSINESS_DAY'
        ? '第' + day + '営業日まで'
        : day + '日まで'
      return {
        number: ('0' + day).slice(-2),
        label,
        items: this.submissionDueItems
      }
    },

    /**
     * 画面のいちばん上の挨拶。
     *
     * **名前を呼び捨てにしない。**「さん」を付ける。名前が読めないときは
     * 名前ごと出さない——「おはようございます、さん」になるくらいなら、
     * 挨拶だけのほうがよい（以前は「メンバー」と出していた）。
     */
    greeting () {
      const hour = this.now.getHours()
      const time = hour < 11 ? 'おはようございます' : 'おつかれさまです'
      return this.userName ? time + '、' + this.userName + 'さん' : time
    },
    formattedDate () { return moment(this.now).format('YYYY年 M月 D日') },
    formattedDateWithWeekday () { const weekdays = ['日', '月', '火', '水', '木', '金', '土']; return moment(this.now).format('YYYY年 M月 D日') + '（' + weekdays[this.now.getDay()] + '）' },
    formattedTime () { return moment(this.now).format('HH:mm:ss') },
    stampingTimestamp () { return moment(this.now).format('YYYY/MM/DD HH:mm') },
    currentYearMonth () { return moment(this.now).format('YYYY年M月') },
    attendanceStatusLabel () {
      if (this.stampingStatusError) return '取得失敗'
      return ({
         NOT_STARTED: '未打刻',
         SCHEDULED: '定時候補あり',
        WORKING: '業務中',
        BREAK: '休憩中',
        FINISHED: '退勤済み'
      })[this.attendanceStatus] || '未打刻'
    },
    endTimeDisplay () {
      if (!this.endTime) return '--:--'
      if (!this.startTime) return this.endTime

      const start = moment(this.startTime, 'HH:mm', true)
      const end = moment(this.endTime, 'HH:mm', true)
      if (!start.isValid() || !end.isValid()) return this.endTime

      return end.isBefore(start)
        ? `${this.endTime}（翌日）`
        : this.endTime
    },
    canStartAttendance () {
      return this.stampingStatusLoaded && ['NOT_STARTED', 'SCHEDULED'].includes(this.attendanceStatus)
    },
    canEndAttendance () {
      return this.stampingStatusLoaded && this.attendanceStatus === 'WORKING'
    },
    canStartBreak () {
      return this.stampingStatusLoaded &&
        this.attendanceStatus === 'WORKING'
    },
    canEndBreak () {
      return this.stampingStatusLoaded && this.attendanceStatus === 'BREAK'
    },
    breakStartLabel () {
      if (!this.breakStartedAt) return ''
      const start = moment(this.breakStartedAt, 'YYYY/MM/DD HH:mm', true)
      if (!start.isValid()) return ''
      return start.format('MM/DD HH:mm')
    },
    breakElapsedLabel () {
      if (!this.breakStartedAt) return ''
      const start = moment(this.breakStartedAt, 'YYYY/MM/DD HH:mm', true)
      if (!start.isValid()) return ''
      const minutes = Math.max(0, moment(this.now).diff(start, 'minutes'))
      const hours = Math.floor(minutes / 60)
      const restMinutes = String(minutes % 60).padStart(2, '0')
      return `${hours}:${restMinutes}`
    },
    breakStartButtonLabel () {
      return this.useProjectBreakFlg ? '追加休憩開始' : '休憩開始'
    },
    breakEndButtonLabel () {
      return this.useProjectBreakFlg ? '追加休憩終了' : '休憩終了'
    },
    breakSummaryDescription () {
      return this.useProjectBreakFlg ? '固定休憩＋追加休憩' : '打刻した休憩の累計'
    },
    breakStartDescription () {
      if (this.attendanceStatus === 'BREAK') return '現在休憩中です'
      if (this.attendanceStatus !== 'WORKING') return '出勤後に利用可能'
      if (this.useProjectBreakFlg) return '固定休憩とは別に記録'
      return '休憩を記録する'
    },
    breakEndDescription () {
      if (this.attendanceStatus !== 'BREAK') return '休憩開始後に利用可能'
      const start = this.breakStartLabel ? `${this.breakStartLabel} 開始` : '休憩中'
      const elapsed = this.breakElapsedLabel ? `（${this.breakElapsedLabel}経過）` : ''
      return start + elapsed
    },
    summaryItems () {
      return [
        { label: '総勤務時間', value: this.formatDuration(this.monthlySummary.workTime) },
        { label: '時間外', value: this.formatDuration(this.monthlySummary.overTime) },
        { label: '休日勤務', value: this.formatDuration(this.monthlySummary.holidayTime) },
        { label: '深夜勤務', value: this.formatDuration(this.monthlySummary.nightTime) },
        { label: '有休使用', value: (this.monthlySummary.paidHolidayUsed || 0) + '日' },
        { label: '有休残数', value: (this.monthlySummary.paidHolidayRemain || 0) + '日' }
      ]
    },
    monthlyDocuments () {
      const definitions = [
        { documentType: '01', documentName: '勤務表', icon: 'cil-calendar-check' },
        { documentType: '02', documentName: '業務報告', icon: 'cil-description' },
        { documentType: '03', documentName: '経費', icon: 'cil-wallet' },
        { documentType: '04', documentName: '通勤費', icon: 'cil-bus-alt' }
      ]
      return definitions.map(definition => {
        const status = this.submissions.find(item => String(item.documentType) === definition.documentType)
        return { ...definition, statusName: status ? status.statusName : '未登録', tone: this.getStatusTone(status ? String(status.status) : '') }
      })
    }
  },
  mounted () {
    this.timerId = window.setInterval(() => { this.now = new Date() }, 1000)
    this.fetchDashboard()
    this.fetchDashboardAuthority()
    this.fetchDashboardInsights()
    this.resolveAttendanceOverviewView()
    this.fetchProjectBreakSetting()
    this.fetchStampingStatus()
  },
  beforeDestroy () { if (this.timerId) window.clearInterval(this.timerId) },
  methods: {
    formatDuration (value) { if (!value) return '00:00'; return String(value).substring(0, 5) },
    getStatusTone (status) { return ({ '1': 'neutral', '2': 'pending', '3': 'rejected', '4': 'approved' })[status] || 'empty' },
    fetchDashboard () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/dashboard').then(response => {
        const data = response.data || {}
        this.userName = data.userName || ''
        this.actionCounts = data.actionCounts || {}
        this.monthlySummary = data.monthlySummary || {}
        // **協定が無ければ空のまま。**部品の側が自分で出ないと決める。
        this.myOvertimeLimit = data.myOvertimeLimit || {}
        this.submissions = data.submissions || []
        /*
         * **読めなければ出さない。**空文字にしておくと v-if が偽になり、
         * カレンダーの節そのものが出ない。ここで既定のURLへ倒すと、
         * 2026-08-27 まで起きていた漏れが戻る。
         */
        this.calendarEmbedUrl = data.dashboardCalendarEmbedUrl || ''
        /* 締めも同じ。**読めなければ、期限の枠を出さない。** */
        this.submissionDueKind = data.submissionDueKind || ''
        this.submissionDueDay = data.submissionDueDay || null
        this.submissionDueItems = data.submissionDueItems || ''
        /* お知らせも同じ。**読めなければ、カードごと出さない。** */
        this.notices = Array.isArray(data.notices) ? data.notices : []
      }).catch(error => console.error('ダッシュボード情報取得失敗:', safeErrorDigest(error)))
    },
    /**
     * お知らせの日付。
     *
     * **読めない値のときは何も出さない。**「Invalid date」と出すくらいなら
     * 日付の欄を空けたほうがよい。
     */
    noticeDate (notice) {
      const at = moment(notice && notice.publishedAt)
      return at.isValid() ? at.format('YYYY.MM.DD') : ''
    },

    /**
     * 見た目の色分け。**知らない種類は system 扱いにする。**
     *
     * ここで種類の名前をそのまま class へ入れると、増えた種類の色が
     * 決まっていないぶんだけ素の見た目になる。
     */
    noticeTone (notice) {
      return (notice && notice.category) === 'UPDATE' ? 'update' : 'system'
    },

    fetchDashboardAuthority () {
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/authority')
        .then(response => {
          this.authorityName = String(response.data || '').trim()
          this.managementView = isDashboardManagementAuthority(this.authorityName)
        })
        .catch(error => {
          console.error('ダッシュボード権限情報取得失敗:', safeErrorDigest(error))
        })
    },
    /*
     * 今日の勤怠状況。**権限が無ければ問い合わせもしない。**
     *
     * 呼んで403を受けると、画面には出ているのに中身が出ない状態になり、
     * 利用者からは壊れて見える（打刻カードで同じことが起きている）。
     * /access-context の permissions を先に見て、あるときだけ出す。
     *
     * 稼働ヒートマップ（WORKFORCE_VIEW）より狭く、経営管理者以上に限る。
     */
    /*
     * offset は画面の「次へ」から来る。retry のときは undefined なので
     * 先頭に戻る。1000人規模でも1枚のカードに出し切らないための仕組み。
     */
    fetchAttendanceOverview (offset) {
      // 古い応答が新しい条件の一覧を上書きしないよう、連番で見分ける。
      const requestId = this.attendanceOverviewRequestId + 1
      this.attendanceOverviewRequestId = requestId

      this.attendanceOverviewLoading = true
      this.attendanceOverviewError = false
      const params = {}
      if (Number.isFinite(offset) && offset > 0) {
        params.offset = offset
      }
      /*
       * **絞り込みはサーバへ渡す。**画面で絞ると、ページに載っている
       * 100名だけが対象になり、別のページにいる人を探せない。
       * 月次締めのコックピットがその作りになっている。
       */
      if (this.attendanceOverviewKeyword) {
        params.keyword = this.attendanceOverviewKeyword
      }
      if (this.attendanceOverviewStatus) {
        params.status = this.attendanceOverviewStatus
      }
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/attendance-overview', { params })
        .then(response => {
          if (requestId !== this.attendanceOverviewRequestId) return
          this.attendanceOverview = response.data || {}
          this.attendanceOverviewError = false
        })
        .catch(error => {
          if (requestId !== this.attendanceOverviewRequestId) return
          this.attendanceOverviewError = true
          console.error('今日の勤怠状況の取得に失敗しました:', safeErrorDigest(error))
        })
        .finally(() => {
          if (requestId === this.attendanceOverviewRequestId) {
            this.attendanceOverviewLoading = false
          }
        })
    },

    /*
     * 絞り込みを変えたら先頭のページから取り直す。
     *
     * offset を持ったままだと、3ページ目を見ている状態で絞った結果が
     * 1ページしか無いときに空で表示される。
     */
    applyAttendanceOverviewFilter (condition) {
      this.attendanceOverviewKeyword = (condition && condition.keyword) || ''
      this.attendanceOverviewStatus = (condition && condition.status) || ''
      return this.fetchAttendanceOverview()
    },

    /*
     * 月次締めの絞り込み。offset を渡さないので先頭のページから取り直す。
     *
     * offset を持ったままだと、3ページ目を見ている状態で絞った結果が
     * 1ページしか無いときに空で表示される。
     */
    applyCloseCondition (condition) {
      this.closeQuery = (condition && condition.query) || ''
      this.closeState = (condition && condition.state) || ''
      return this.fetchDashboardInsights()
    },

    /** 見せてよい相手か。permissions を正本にする（表示名では判断しない）。 */
    resolveAttendanceOverviewView () {
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/access-context')
        .then(response => {
          const context = response.data || {}
          const permissions = Array.isArray(context.permissions) ? context.permissions : []
          const features = Array.isArray(context.features) ? context.features : []
          this.attendanceOverviewView =
            permissions.includes('MANAGEMENT_INSIGHT_VIEW') &&
            features.includes('MANAGEMENT_INSIGHT')
          /*
           * 36協定の上限は、稼働ヒートマップと同じ狭さに加えて
           * **労務法令の判定を出してよい段階か**でも決まる。
           * **同じ1回の応答から決める。**別に /access-context を呼ぶと、
           * ダッシュボードを開くたびに同じ問い合わせが2回飛ぶ。
           *
           * 出さない段階では**問い合わせ自体を送らない。**
           * 送ったうえで空にすると、開くたびに全社ぶんの判定が走る
           * ——非表示なのではなく、計算して捨てていることになる。
           */
          this.overtimeLimitView = this.attendanceOverviewView
            && features.includes('OCCUPATIONAL_LEGAL_COMPLIANCE')
          const pending = []
          if (this.attendanceOverviewView) {
            pending.push(this.fetchAttendanceOverview())
          }
          if (this.overtimeLimitView) {
            pending.push(this.fetchOvertimeLimitStatus())
          }
          return pending.length ? Promise.all(pending) : null
        })
        .catch(error => {
          // 取れなければ出さない。**出したうえで空にするより、出さない方がよい。**
          this.attendanceOverviewView = false
          this.overtimeLimitView = false
          console.error('権限情報の取得に失敗しました:', safeErrorDigest(error))
        })
    },

    /**
     * 36協定の上限状況。**専用画面と同じ口を1回だけ呼ぶ。**
     *
     * ダッシュボードに出すのは人数と上位3名だけなので pageSize=3。
     * 対象月は**専用画面と同じ既定（前月）**を使う。別々に決めると、
     * カードの人数と専用画面の人数が食い違う。
     * 失敗したらカードを出さない。**空のカードを出すより、出さない方がよい。**
     */
    fetchOvertimeLimitStatus () {
      const condition = overtimeLimitConditionParams(defaultOvertimeLimitMonth())
      if (!condition) return Promise.resolve()

      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/overtime-limit-status', {
          params: { ...condition, currentPage: 1, pageSize: 3 }
        })
        .then(response => {
          this.overtimeLimitStatus = response.data || {}
        })
        .catch(error => {
          this.overtimeLimitStatus = {}
          console.error('36協定の上限状況の取得に失敗しました:', safeErrorDigest(error))
        })
    },

    /*
     * closeOffset は月次締めの「次へ」から来る。retry のときは undefined
     * なので先頭へ戻る。締め一覧は1000人規模でもページで開けるようにする。
     */
    fetchDashboardInsights (closeOffset) {
      /*
       * 以前はここで「読み込み中なら何もしない」としていた。条件を変えた
       * 直後は前の要求が飛んでいるので、**新しい条件が投げられないまま
       * 古い一覧が残る**。投げたうえで、古い応答を捨てる形にする。
       */
      const requestId = this.insightRequestId + 1
      this.insightRequestId = requestId

      this.insightLoading = true
      this.insightError = false
      const params = {}
      if (Number.isFinite(closeOffset) && closeOffset > 0) {
        params.closeOffset = closeOffset
      }
      /*
       * **月次締めの検索・絞り込みはサーバへ渡す。**
       * 画面で絞ると、載っている20人だけが対象になり、
       * 2ページ目にいる人を探せない。件数は全員から数えているので
       * 母数も食い違う。今日の勤怠状況と同じ作りに揃えた。
       */
      if (this.closeQuery) {
        params.closeQuery = this.closeQuery
      }
      if (this.closeState) {
        params.closeState = this.closeState
      }

      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/dashboard/insights', { params })
        .then(response => {
          if (requestId !== this.insightRequestId) return
          const data = response.data || {}
          this.authorityName = String(data.authorityName || this.authorityName || '').trim()
          this.managementView = data.managementView === true ||
            isDashboardManagementAuthority(this.authorityName)
          this.actionItems = Array.isArray(data.actionItems) ? data.actionItems : []
          this.workProjection = data.workProjection || {}
          this.closeCockpit = data.closeCockpit || {}
          this.approvalAging = data.approvalAging || {}
          this.insightError = false
        })
        .catch(error => {
          if (requestId !== this.insightRequestId) return
          this.insightError = true
          console.error('ダッシュボード判断支援情報取得失敗:', safeErrorDigest(error))
        })
        .finally(() => {
          if (requestId === this.insightRequestId) this.insightLoading = false
        })
    },
    fetchProjectBreakSetting () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/initial')
        .then(response => { this.useProjectBreakFlg = response.data === true })
        .catch(error => console.error('休憩自動設定フラグ取得失敗:', safeErrorDigest(error)))
    },
    fetchStampingStatus () {
      this.stampingStatusLoading = true
      this.stampingStatusError = false
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/status')
        .then(response => {
          const status = response.data || {}
          this.attendanceStatus = status.status || 'NOT_STARTED'
          this.businessDate = status.businessDate || ''
          this.startTime = status.startTime || ''
          this.endTime = status.endTime || ''
          this.breakStartedAt = status.breakStartedAt || ''
          this.breakTime = status.breakTime || '00:00'
          this.nightRest = normalizeTimeValue(
            status.nightRest,
            '00:00'
          )
          this.stampingStatusLoaded = true
          this.stampingStatusError = false
        })
        .catch(error => {
          this.stampingStatusLoaded = false
          this.stampingStatusError = true
          console.error('打刻状態取得失敗:', safeErrorDigest(error))
        })
        .finally(() => { this.stampingStatusLoading = false })
    },
    formatBusinessDate (value) {
      const date = moment(value, 'YYYY-MM-DD', true)
      return date.isValid() ? date.format('YYYY/MM/DD') : value
    },
    handleTime (type) {
      if (this.submitFlg || this.loading || this.stampingStatusLoading) return

      const replacingScheduled = type === 'start' && this.attendanceStatus === 'SCHEDULED'
      if (replacingScheduled && !window.confirm(
        '勤務表に保存済みの定時候補を取り消し、現在時刻を出勤実績として記録します。よろしいですか？'
      )) return

      const payload = {
        button_type: type,
        timestamp: this.stampingTimestamp,
        replace_scheduled: replacingScheduled
      }
      this.submitFlg = true
      this.loading = true
      axios.post(process.env.VUE_APP_API_ORIGIN + '/stamping/update', payload)
        .then(() => Promise.all([
          this.fetchStampingStatus(),
          this.fetchDashboard(),
          this.fetchDashboardInsights()
        ]))
        .then(() => { alert(ConstList.SUCCESS_STAMPING) })
        .catch(error => {
          console.error('打刻送信失敗:', safeErrorDigest(error))
          alert(backendErrorMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR))
          return this.fetchStampingStatus()
        })
        .finally(() => { this.submitFlg = false; this.loading = false })
    }
  }
}
</script>

<style scoped>
.preika-dashboard{--preika-primary:#0a66b2;--preika-navy:#0c2642;--preika-ink:#172b3f;--preika-muted:#6f8193;--preika-border:#e5edf4;padding:10px 4px 36px;color:var(--preika-ink)}
.hero-panel{position:relative;display:flex;min-height:320px;margin-bottom:24px;overflow:hidden;border-radius:24px;background:linear-gradient(120deg,#064f91 0%,#0b70bc 48%,#0a5ca4 100%);box-shadow:0 24px 60px rgba(5,66,118,.24)}
.hero-panel__content{position:relative;z-index:3;width:60%;padding:42px 52px}.hero-brand{display:flex;align-items:center;margin-bottom:28px}.hero-brand__logo{width:150px;max-height:42px;margin-right:16px;object-fit:contain;filter:brightness(0) invert(1)}.hero-brand__badge{padding:6px 10px;border:1px solid rgba(255,255,255,.26);border-radius:99px;color:rgba(255,255,255,.8);font-size:10px;font-weight:700;letter-spacing:.13em}.hero-panel__eyebrow{margin:0 0 10px;color:#a9dcff;font-size:14px;font-weight:700}.hero-panel__title{margin:0;color:#fff;font-size:38px;font-weight:700;line-height:1.28}.hero-panel__title span{color:#d9f1ff}.hero-panel__description{max-width:580px;margin:18px 0 24px;color:rgba(255,255,255,.78);font-size:14px;line-height:1.9}.hero-panel__meta{display:flex;gap:12px}.hero-meta-item{display:inline-flex;align-items:center;gap:8px;padding:9px 14px;border:1px solid rgba(255,255,255,.15);border-radius:10px;background:rgba(255,255,255,.08);color:#fff;font-size:13px}
.hero-visual{position:relative;z-index:2;flex:1}.hero-visual__orbit{position:absolute;border:1px solid rgba(255,255,255,.13);border-radius:50%}.hero-visual__orbit--one{top:35px;right:18px;width:290px;height:290px}.hero-visual__orbit--two{top:82px;right:62px;width:200px;height:200px}.hero-visual__card{position:absolute;border:1px solid rgba(255,255,255,.28);background:rgba(255,255,255,.94);box-shadow:0 28px 55px rgba(0,27,59,.28)}.hero-visual__card--rear{top:58px;right:32px;width:230px;height:145px;padding:28px 24px;border-radius:18px;transform:rotate(8deg);opacity:.8}.hero-visual__card--front{top:87px;right:110px;width:245px;height:190px;padding:20px;border-radius:20px;transform:rotate(-4deg)}.visual-line{display:block;width:100%;height:9px;margin-bottom:14px;border-radius:20px;background:#dce8f1}.visual-line--short{width:34%;background:#83c9f4}.visual-line--middle{width:72%}.visual-card__header{display:flex;align-items:center;justify-content:space-between}.visual-avatar{display:inline-flex;width:38px;height:38px;align-items:center;justify-content:center;border-radius:12px;background:#0871be;color:#fff;font-weight:800}.visual-status{width:62px;height:8px;border-radius:20px;background:#dfeaf2}.visual-chart{display:flex;height:85px;align-items:flex-end;gap:10px;margin-top:20px}.visual-chart span{flex:1;border-radius:7px 7px 3px 3px;background:linear-gradient(180deg,#43b8ee,#0871be)}.visual-card__footer{display:flex;gap:8px;margin-top:14px}.visual-card__footer span{width:42px;height:7px;border-radius:10px;background:#dfe9f1}
.dashboard-grid{display:grid;gap:24px;margin-bottom:24px}.dashboard-grid--top{grid-template-columns:minmax(0,1.12fr) minmax(350px,.88fr)}.dashboard-grid--overview{grid-template-columns:minmax(0,1.08fr) minmax(350px,.92fr)}.dashboard-grid--middle{grid-template-columns:minmax(0,1.06fr) minmax(340px,.94fr)}.dashboard-grid--single{grid-template-columns:1fr}.dashboard-card{overflow:hidden;border:1px solid var(--preika-border);border-radius:20px;background:#fff;box-shadow:0 12px 32px rgba(19,54,83,.07)}.attendance-card,.quick-card,.notice-card,.submission-card,.monthly-summary-card,.monthly-status-card{padding:28px}.card-heading{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:22px}.card-heading__eyebrow{margin:0 0 4px;color:var(--preika-primary);font-size:10px;font-weight:800;letter-spacing:.16em}.card-heading h2{margin:0;font-size:21px}.card-heading__icon{width:24px;color:#92a9ba}
.status-pill{display:inline-flex;align-items:center;gap:7px;padding:7px 11px;border-radius:99px;background:#f1f5f8;color:#7b8d9b;font-size:11px;font-weight:700}.status-pill__dot{width:7px;height:7px;border-radius:50%;background:#aebbc5}.status-pill--active{background:#e9f7f1;color:#148055}.status-pill--active .status-pill__dot{background:#24ad77}.status-pill--break{background:#fff2e5;color:#b9630c}.status-pill--break .status-pill__dot{background:#e58a27}.status-pill--error{background:#fdeaea;color:#ad3f3f}.status-pill--error .status-pill__dot{background:#d95a5a}.attendance-time{display:flex;align-items:baseline;gap:16px;margin-bottom:22px}.attendance-time strong{color:var(--preika-navy);font-size:38px}.attendance-time span{color:var(--preika-muted);font-size:13px}.attendance-actions{display:grid;grid-template-columns:1fr 1fr;gap:12px}.attendance-button{display:flex;min-height:76px;align-items:center;gap:14px;padding:13px 15px;border:1px solid #e2eaf0;border-radius:14px;background:#fff;text-align:left;transition:.2s}.attendance-button:not(:disabled):hover{border-color:#a8d5ef;box-shadow:0 10px 24px rgba(10,102,178,.12);transform:translateY(-2px)}.attendance-button:disabled{opacity:.5}.attendance-button__icon{display:inline-flex;width:42px;height:42px;align-items:center;justify-content:center;border-radius:12px}.attendance-button strong,.attendance-button small{display:block}.attendance-button small{color:#8293a1;font-size:10px}.attendance-button--start .attendance-button__icon{background:#e5f6ef;color:#159565}.attendance-button--end .attendance-button__icon{background:#e8f2fb;color:#0a66b2}.attendance-button--break .attendance-button__icon{background:#fff2e5;color:#d97a16}.attendance-button--return .attendance-button__icon{background:#eeeafc;color:#6550b9}.attendance-stamp-result{margin-top:18px;padding:15px;border:1px solid #e2ebf2;border-radius:15px;background:linear-gradient(180deg,#fbfdff,#f5f9fc)}.attendance-stamp-result__header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:11px}.attendance-stamp-result__header p{margin:0 0 2px;color:#0a66b2;font-size:8px;font-weight:800;letter-spacing:.14em}.attendance-stamp-result__header h3{margin:0;color:#0c2642;font-size:14px}.attendance-stamp-result__date{padding:5px 8px;border-radius:99px;background:#eaf3fa;color:#527188;font-size:8px;font-weight:700}.attendance-stamp-result__grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px}.attendance-stamp-result__item{position:relative;min-width:0;padding:11px 10px;border:1px solid #e4ebf1;border-radius:11px;background:#fff}.attendance-stamp-result__item::before{position:absolute;top:0;right:10px;left:10px;height:3px;border-radius:0 0 4px 4px;content:''}.attendance-stamp-result__item--start::before{background:#24ad77}.attendance-stamp-result__item--end::before{background:#0a66b2}.attendance-stamp-result__item--break::before{background:#e58a27}.attendance-stamp-result__item--night::before{background:#6550b9}.attendance-stamp-result__label,.attendance-stamp-result__item strong,.attendance-stamp-result__item small{display:block}.attendance-stamp-result__label{margin-top:2px;color:#718594;font-size:8px;font-weight:700}.attendance-stamp-result__item strong{margin:4px 0 1px;overflow:hidden;color:#0c2642;font-size:17px;letter-spacing:.02em;text-overflow:ellipsis;white-space:nowrap}.attendance-stamp-result__item small{color:#93a2ad;font-size:7px}.attendance-status-error{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:18px;padding:14px 15px;border:1px solid #f0cccc;border-radius:13px;background:#fff7f7}.attendance-status-error strong,.attendance-status-error small{display:block}.attendance-status-error strong{color:#9f3939;font-size:11px}.attendance-status-error small{margin-top:3px;color:#9b6a6a;font-size:8px}.attendance-status-error button{padding:7px 11px;border:1px solid #dca8a8;border-radius:9px;background:#fff;color:#a13d3d;font-size:9px;font-weight:700}.attendance-status-error button:disabled{opacity:.5}.attendance-loading{margin-top:16px;color:var(--preika-primary);font-size:12px}
.quick-menu{display:grid;grid-template-columns:1fr 1fr;gap:10px}.quick-menu__item{display:flex;min-height:78px;align-items:center;gap:12px;padding:13px;border:1px solid #e6edf3;border-radius:14px;color:inherit;text-decoration:none}.quick-menu__icon{display:inline-flex;width:40px;height:40px;align-items:center;justify-content:center;border-radius:12px}.quick-menu__icon--blue{background:#e5f2fc;color:#0a66b2}.quick-menu__icon--navy{background:#e9eef4;color:#213e59}.quick-menu__icon--green{background:#e5f6ef;color:#16875f}.quick-menu__icon--orange{background:#fff0df;color:#cc7318}.quick-menu__icon--purple{background:#eee9fb;color:#6b52bd}.quick-menu__icon--cyan{background:#e3f5f7;color:#16808a}.quick-menu__text{flex:1}.quick-menu__text strong,.quick-menu__text small{display:block}.quick-menu__text small{color:#8393a0;font-size:9px}.quick-menu__arrow{width:14px;color:#b0bdc7}
.metric-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;margin-bottom:24px}.metric-card{display:flex;min-height:118px;align-items:center;gap:16px;padding:20px;border:1px solid var(--preika-border);border-radius:18px;background:#fff;color:inherit;box-shadow:0 10px 28px rgba(19,54,83,.06);text-decoration:none}.metric-card__icon{display:inline-flex;width:48px;height:48px;align-items:center;justify-content:center;border-radius:15px}.metric-card--blue .metric-card__icon{background:#e4f2fc;color:#0a66b2}.metric-card--orange .metric-card__icon{background:#fff0df;color:#cf7315}.metric-card--purple .metric-card__icon{background:#eee9fb;color:#6851b7}.metric-card--green .metric-card__icon{background:#e4f6ed;color:#14835b}.metric-card__body small,.metric-card__body strong,.metric-card__body span{display:block}.metric-card__body small{color:#778997;font-size:10px}.metric-card__body strong{margin:3px 0;font-size:28px}.metric-card__body span{color:#95a3ae;font-size:9px}.metric-card__body .metric-card__time{font-size:23px}
.summary-month{padding:6px 10px;border-radius:8px;background:#eef5fa;color:#607789;font-size:10px}.summary-list{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.summary-item{padding:17px;border-radius:14px;background:#f7f9fb}.summary-item__label{display:block;margin-bottom:8px;color:#788b99;font-size:10px}.summary-item strong{font-size:18px}.text-link{display:inline-flex;align-items:center;gap:6px;color:var(--preika-primary);font-size:10px}.document-status-list{display:grid;gap:9px}.document-status{display:grid;grid-template-columns:38px 1fr auto;align-items:center;gap:12px;padding:12px 14px;border:1px solid #e8eef3;border-radius:13px}.document-status__icon{display:inline-flex;width:36px;height:36px;align-items:center;justify-content:center;border-radius:10px;background:#edf5fa;color:var(--preika-primary)}.document-status__name{font-size:12px;font-weight:700}.document-status__badge{min-width:64px;padding:5px 8px;border-radius:99px;text-align:center;font-size:9px;font-weight:800}.document-status__badge--neutral{background:#f0f3f5;color:#6f7f8b}.document-status__badge--pending{background:#fff3d9;color:#9d6900}.document-status__badge--rejected{background:#fde8e8;color:#b44444}.document-status__badge--approved{background:#e4f6ed;color:#14764f}.document-status__badge--empty{background:#f3f5f7;color:#9aa6ae}
.notice-count{color:#8798a5;font-size:10px}.notice-list{border-top:1px solid #edf2f6}.notice-item{display:grid;grid-template-columns:92px 1fr;gap:18px;padding:17px 2px;border-bottom:1px solid #edf2f6}.notice-item__date{color:#8a9aa7;font-size:11px}.notice-item__content{display:flex;align-items:center;gap:12px}.notice-item__content p{margin:0;font-size:12px}.notice-item__category{padding:4px 8px;border-radius:6px;font-size:9px}.notice-item__category--update{background:#e5f2fc;color:#0a66b2}.notice-item__category--system{background:#fff0df;color:#bc6913}.submission-guide{display:grid;gap:12px}.submission-guide__item{display:flex;align-items:center;gap:16px;padding:17px;border-radius:15px}.submission-guide__item--monthly{background:#eef6fc}.submission-guide__item--anytime{background:#eef8f4}.submission-guide__number{display:inline-flex;width:48px;height:48px;align-items:center;justify-content:center;border-radius:14px;background:#fff;color:var(--preika-primary);font-weight:800}.submission-guide p{margin:0;color:#6f8190;font-size:10px}.submission-link{display:flex;justify-content:space-between;margin-top:17px;padding:12px 15px;border-radius:11px;background:var(--preika-navy);color:#fff;text-decoration:none}.calendar-card__heading{margin:0;padding:25px 28px 20px}.calendar-frame{min-height:520px;border-top:1px solid #e8eef3}.calendar-frame iframe{display:block;width:100%;height:600px;border:0}
@media(max-width:1199px){.hero-panel__content{width:62%;padding:38px}.hero-visual__card--front{right:55px}.dashboard-grid--top,.dashboard-grid--middle,.dashboard-grid--overview{grid-template-columns:1fr}}
@media(max-width:900px){.metric-grid{grid-template-columns:1fr 1fr}}
@media(max-width:767px){.hero-panel{min-height:auto;border-radius:18px}.hero-panel__content{width:100%;padding:30px 24px}.hero-brand{align-items:flex-start;flex-direction:column;gap:10px}.hero-panel__title{font-size:29px}.hero-visual{display:none}.attendance-card,.quick-card,.notice-card,.submission-card,.monthly-summary-card,.monthly-status-card{padding:22px 18px}.attendance-time{align-items:flex-start;flex-direction:column}.attendance-actions,.quick-menu,.metric-grid,.summary-list{grid-template-columns:1fr}.attendance-stamp-result__header{align-items:flex-start;flex-direction:column}.attendance-stamp-result__grid{grid-template-columns:1fr 1fr}.attendance-status-error{align-items:flex-start;flex-direction:column}.attendance-status-error button{width:100%}.notice-item{grid-template-columns:1fr}.notice-item__content{align-items:flex-start;flex-direction:column}}
.status-pill--scheduled{background:#eef1ff;color:#5b57a8}
.status-pill--scheduled .status-pill__dot{background:#746ed0}
.attendance-scheduled-note{margin-bottom:10px;padding:9px 11px;border-radius:9px;background:#eef1ff;color:#55538e;font-size:9px;line-height:1.6}

.attendance-break-note{display:flex;align-items:center;gap:8px;margin-top:12px;padding:10px 12px;border:1px solid #f0d7b9;border-radius:11px;background:#fffaf3;color:#80552a;font-size:10px;line-height:1.55}.attendance-break-note strong{flex:0 0 auto;color:#9a5f1f}.attendance-break-note span{min-width:0}
</style>
